import { Button, Loader } from '@aws-amplify/ui-react';
import { useContext, useEffect, useState } from 'react';
import { TbArrowRight } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import LoadingBars from '../../custom-components/LoadingBars';
import { GlobalContext } from '../../global-context';
import { toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { ExportContext } from '../ExportDetail';
import { optionKeysOf, optionsOf } from './exportShapes';
import { ContactExportOptions } from './options/ContactExportOptions';
import { InstitutionExportOptions } from './options/InstitutionExportOptions';

export function ExportStepOptions() {
  const exportContext = useContext(ExportContext);
  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();
  const [options, setOptions] = useState<Record<string, any>>({});
  const [rangeFrom, setRangeFrom] = useState<number | undefined>();
  const [rangeTo, setRangeTo] = useState<number | undefined>();
  const [isValid, setIsValid] = useState(true);
  const [isRangeValid, setIsRangeValid] = useState(true);

  // Reset saving state on mount (in case previous step navigated while saving)
  useEffect(() => {
    exportContext?.setIsSaving(false);
  }, []);

  const saving = exportContext?.isSaving ?? false;
  const setSaving = exportContext?.setIsSaving ?? (() => {});

  if (!exportContext) {
    return (
      <div className="content content-1200">
        <div className="export-step">
          <LoadingBars numberOfRows={5} />
        </div>
      </div>
    );
  }

  const entity = exportContext.exportData?.entity;
  const target = exportContext.exportData?.target;
  const estimatedCount = exportContext.exportData?.estimated_count ?? undefined;
  const savedOptions = optionKeysOf(exportContext.exportData?.options);
  const isFromMatchRun = !!exportContext.exportData?.match_id;
  const isSelectable = ['TARGET_SELECTION', 'FIELDS_SELECTION', 'OPTIONS_SELECTION'].includes(exportContext.exportData?.status);
  const readOnly = exportContext.readOnly ?? false;

  const handleContinue = async () => {
    setSaving(true);
    try {
      // The range goes with the options, as before: unset clears it.
      await eduleadV4.exports.update(
        {
          id: exportContext.exportId,
          update_mask: 'options,range_from,range_to',
          body: { options: optionsOf(options), range_from: rangeFrom, range_to: rangeTo }
        },
        { team: exportContext.teamId }
      );
      await exportContext.refreshExport();
      navigate(`/exports/${exportContext.exportId}/review`);
    } catch (error) {
      toastV4Error(error, 'Failed to save options');
      setSaving(false);
    }
  };

  return (
    <div className="content content-1200">
      <div className="export-step">
        {entity === 'CONTACT' && (
          <ContactExportOptions
            value={savedOptions}
            onChange={setOptions}
            target={target}
            disabled={!isSelectable || readOnly}
            estimatedCount={estimatedCount}
            onValidChange={setIsValid}
            savedSearchName={exportContext.exportData?.search_name}
            userFirstName={globalContext.authInfo?.userAttributes?.given_name}
            rangeFrom={exportContext.exportData?.range_from}
            rangeTo={exportContext.exportData?.range_to}
            onRangeChange={({ rangeFrom: rf, rangeTo: rt }) => {
              setRangeFrom(rf);
              setRangeTo(rt);
            }}
            onRangeValidChange={setIsRangeValid}
          />
        )}
        {entity === 'INSTITUTION' && (
          <InstitutionExportOptions
            value={savedOptions}
            onChange={setOptions}
            target={target}
            disabled={!isSelectable || readOnly}
            isCrmTarget={exportContext?.isCrmTarget ?? false}
            isFromMatchRun={isFromMatchRun}
            estimatedCount={estimatedCount}
            rangeFrom={exportContext.exportData?.range_from}
            rangeTo={exportContext.exportData?.range_to}
            onRangeChange={({ rangeFrom: rf, rangeTo: rt }) => {
              setRangeFrom(rf);
              setRangeTo(rt);
            }}
            onRangeValidChange={setIsRangeValid}
          />
        )}
      </div>
      {isSelectable && !readOnly && (
        <div className="export-step__actions">
          {saving && <Loader size="small" />}
          <Button variation="primary" size="small" onClick={handleContinue} isDisabled={saving || !isValid || !isRangeValid}>
            Save and Continue<TbArrowRight style={{ marginLeft: 8 }} />
          </Button>
        </div>
      )}
    </div>
  );
}
